import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Text,
  Td,
  Tr,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon } from "@chakra-ui/icons";
import { FaUserClock } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { listConsultants } from "../../../api/consultantsApi";
import { listClients } from "../../../api/clientsApi";
import {
  MATCH_ASSIGNMENT_STATUSES,
  CONTRACT_TYPE_COLORS,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  DataTable,
  StatusBadge,
  FilterBar,
  FilterSearch,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import ConsultantDetailModal from "./ConsultantDetailModal";
import ClientDetailModal from "./ClientDetailModal";

const SORT_NULL = Symbol("SORT_NULL");

// Filter selects share the FilterBar control height (31px) used across the
// migrated portal list pages.
const FILTER_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontWeight: 600,
};

// Date/number range inputs in the secondary filter row.
const RANGE_INPUT_SX = {
  height: "31px !important",
  fontWeight: 600,
  bg: "white",
};

const isNullish = (v) =>
  v === null || v === undefined || v === "" || v === "-" || v === SORT_NULL;

const getEffectiveBenchDays = (c) => {
  const contractType =
    c?.contractType ??
    c?.contract_type ??
    (Array.isArray(c?.currentContractTypes || c?.current_contract_types)
      ? (c.currentContractTypes || c.current_contract_types)[0]
      : null);
  if (contractType !== "permanent_contract") return 0;
  return c?.benchDays ?? c?.bench_days ?? 0;
};

const compareValues = (a, b, type) => {
  if (type === "date") {
    const ta = a == null ? NaN : new Date(a).getTime();
    const tb = b == null ? NaN : new Date(b).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return ta - tb;
  }
  if (type === "number") {
    const na = Number(a);
    const nb = Number(b);
    const aBad = !Number.isFinite(na);
    const bBad = !Number.isFinite(nb);
    if (aBad && bBad) return 0;
    if (aBad) return 1;
    if (bBad) return -1;
    return na - nb;
  }
  if (type === "boolean") {
    return Number(Boolean(a)) - Number(Boolean(b));
  }
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

// Clickable sort header node, rendered inside the DataTable column label so the
// tiny uppercase header stays consistent with the template while remaining
// sortable. Keeps the original data-testid + aria-sort contract.
const SortLabel = ({ column, sortState, onSort, ariaLabelAsc, ariaLabelDesc, align, children }) => {
  const active = sortState.column === column;
  const ariaSort = active
    ? sortState.direction === "asc"
      ? "ascending"
      : "descending"
    : "none";
  return (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap="4px"
      cursor="pointer"
      userSelect="none"
      onClick={() => onSort(column)}
      aria-sort={ariaSort}
      data-testid={`sort-header-${column}`}
      justify={align === "right" ? "flex-end" : "flex-start"}
      w={align === "right" ? "100%" : undefined}
      _hover={{ color: "portal.primary" }}
    >
      <Box as="span">{children}</Box>
      {active && sortState.direction === "asc" && (
        <TriangleUpIcon boxSize="9px" aria-label={ariaLabelAsc} />
      )}
      {active && sortState.direction === "desc" && (
        <TriangleDownIcon boxSize="9px" aria-label={ariaLabelDesc} />
      )}
    </Flex>
  );
};

const ConsultantTracking = () => {
  const { t, i18n } = useTranslation("portal");
  const { selectedBusinessUnitId, isLoaded: isBUContextLoaded } = useBusinessUnit();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(null);
  const consultantDetailModal = useDisclosure();
  const [selectedConsultantId, setSelectedConsultantId] = useState(null);
  const clientDetailModal = useDisclosure();
  const [selectedClientId, setSelectedClientId] = useState(null);

  // Filter state
  const [search, setSearch] = useState("");
  const [contractTypeFilter, setContractTypeFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [missionEndFrom, setMissionEndFrom] = useState("");
  const [missionEndTo, setMissionEndTo] = useState("");
  const [poEndFrom, setPoEndFrom] = useState("");
  const [poEndTo, setPoEndTo] = useState("");
  const [hasNextMission, setHasNextMission] = useState("");
  const [benchDaysMin, setBenchDaysMin] = useState("");
  const [benchDaysMax, setBenchDaysMax] = useState("");
  const [clientScoreMin, setClientScoreMin] = useState("");
  const [clientScoreMax, setClientScoreMax] = useState("");
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState("");
  const [clients, setClients] = useState([]);

  // Tri-state client-side sort: null → asc → desc → null (default order)
  const [sortState, setSortState] = useState({ column: null, direction: null });

  const handleSort = (column) => {
    setSortState((prev) => {
      if (prev.column !== column) return { column, direction: "asc" };
      if (prev.direction === "asc") return { column, direction: "desc" };
      return { column: null, direction: null };
    });
  };

  // Fetch clients for the filter dropdown
  useEffect(() => {
    if (!isBUContextLoaded) return;
    const fetchClients = async () => {
      try {
        const res = await listClients({ pageSize: 500 }, { buId: selectedBusinessUnitId || undefined });
        setClients(res.items || []);
      } catch {
        setClients([]);
      }
    };
    fetchClients();
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  const fetchData = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const params = {
        search: search || undefined,
        contractType: contractTypeFilter || undefined,
        clientId: clientFilter || undefined,
        missionEndDateFrom: missionEndFrom || undefined,
        missionEndDateTo: missionEndTo || undefined,
        poEndDateFrom: poEndFrom || undefined,
        poEndDateTo: poEndTo || undefined,
        hasNextMission: hasNextMission === "" ? undefined : hasNextMission === "yes",
        benchDaysMin: benchDaysMin === "" ? undefined : Number(benchDaysMin),
        benchDaysMax: benchDaysMax === "" ? undefined : Number(benchDaysMax),
        clientScoreMin: clientScoreMin === "" ? undefined : Number(clientScoreMin),
        clientScoreMax: clientScoreMax === "" ? undefined : Number(clientScoreMax),
        assignmentStatus: assignmentStatusFilter || undefined,
        page,
        pageSize
      };
      const res = await listConsultants(params, {
        buId: selectedBusinessUnitId || undefined
      });
      setItems(res.items || []);
      setTotalItems(res.totalItems || 0);
      setTotalPagesFromApi(typeof res.totalPages === "number" ? res.totalPages : null);
    } catch {
      setItems([]);
      setTotalItems(0);
      setTotalPagesFromApi(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, pageSize, search, contractTypeFilter, clientFilter, missionEndFrom, missionEndTo, poEndFrom, poEndTo, hasNextMission, benchDaysMin, benchDaysMax, clientScoreMin, clientScoreMax, assignmentStatusFilter, selectedBusinessUnitId]);

  const activeFilterCount = useMemo(() => {
    return [
      contractTypeFilter,
      clientFilter,
      missionEndFrom,
      missionEndTo,
      poEndFrom,
      poEndTo,
      hasNextMission,
      benchDaysMin,
      benchDaysMax,
      clientScoreMin,
      clientScoreMax,
      assignmentStatusFilter
    ].filter((v) => v !== "" && v !== null && v !== undefined).length;
  }, [contractTypeFilter, clientFilter, missionEndFrom, missionEndTo, poEndFrom, poEndTo, hasNextMission, benchDaysMin, benchDaysMax, clientScoreMin, clientScoreMax, assignmentStatusFilter]);

  const resetFilters = () => {
    setContractTypeFilter("");
    setClientFilter("");
    setMissionEndFrom("");
    setMissionEndTo("");
    setPoEndFrom("");
    setPoEndTo("");
    setHasNextMission("");
    setBenchDaysMin("");
    setBenchDaysMax("");
    setClientScoreMin("");
    setClientScoreMax("");
    setAssignmentStatusFilter("");
    setPage(1);
  };

  useEffect(() => {
    if (!isBUContextLoaded) return;
    fetchData();
  }, [fetchData, isBUContextLoaded]);

  // Refetch when BU changes
  useEffect(() => {
    if (!isBUContextLoaded) return;
    setPage(1);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBUContextLoaded, selectedBusinessUnitId]);

  // Auto-refresh every 30s (silent)
  useEffect(() => {
    if (!isBUContextLoaded) return;
    const intervalId = setInterval(() => {
      fetchData({ silent: true });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchData, isBUContextLoaded]);

  const totalPages = useMemo(() => {
    return totalPagesFromApi || Math.max(1, Math.ceil(totalItems / pageSize));
  }, [totalPagesFromApi, totalItems, pageSize]);

  const handleView = (id) => {
    setSelectedConsultantId(id);
    consultantDetailModal.onOpen();
  };

  const handleCloseConsultantModal = () => {
    consultantDetailModal.onClose();
    setSelectedConsultantId(null);
  };

  const getContractTypeLabel = (value) => {
    if (!value) return "-";
    const labels = {
      permanent_contract: t("pilotConsultantTracking.list.contractTypes.permanent_contract"),
      freelance: t("pilotConsultantTracking.list.contractTypes.freelance"),
      subcontracting: t("pilotConsultantTracking.list.contractTypes.subcontracting"),
      umbrella_company: t("pilotConsultantTracking.list.contractTypes.umbrella_company")
    };
    return labels[value] || value;
  };

  const formatDate = (value) => (value ? String(value) : "-");

  const formatNumber = (value, options = {}) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    const { minimumFractionDigits = 0, maximumFractionDigits = 0 } = options;
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits,
      maximumFractionDigits
    }).format(num);
  };

  const getAssignmentEndTimestamp = (consultant) => {
    const endDateValue =
      consultant?.assignmentEndDate ??
      consultant?.assignment_end_date ??
      consultant?.currentAssignmentEndDate ??
      consultant?.current_assignment_end_date ??
      consultant?.missionEndDate ??
      consultant?.mission_end_date ??
      consultant?.nextAssignmentEndDate ??
      consultant?.next_assignment_end_date ??
      consultant?.nextConfirmedAssignmentEndDate ??
      consultant?.next_confirmed_assignment_end_date ??
      consultant?.endDate ??
      consultant?.end_date;

    if (!endDateValue) return null;
    const timestamp = new Date(endDateValue).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  };

  const getColumnValue = (consultant, column) => {
    switch (column) {
      case "name":
        return consultant?.name || consultant?.fullName || consultant?.full_name || null;
      case "contractType": {
        const v =
          consultant?.contractType ??
          consultant?.contract_type ??
          (Array.isArray(consultant?.currentContractTypes || consultant?.current_contract_types)
            ? (consultant.currentContractTypes || consultant.current_contract_types)[0]
            : null);
        return v ? getContractTypeLabel(v) : null;
      }
      case "client":
        return consultant?.currentClientName ?? consultant?.current_client_name ?? null;
      case "assignmentEndDate":
        return (
          consultant?.currentAssignmentEndDate ??
          consultant?.current_assignment_end_date ??
          consultant?.assignmentEndDate ??
          consultant?.assignment_end_date ??
          null
        );
      case "purchaseOrderEndDate":
        return (
          consultant?.currentPurchaseOrderEndDate ??
          consultant?.current_purchase_order_end_date ??
          consultant?.purchaseOrderEndDate ??
          consultant?.purchase_order_end_date ??
          null
        );
      case "cumulativeDaysWithClient":
        return consultant?.cumulativeDaysWithClient ?? consultant?.cumulative_days_with_client ?? null;
      case "numberOfClients":
        return consultant?.numberOfClients ?? consultant?.number_of_clients ?? null;
      case "cumulativeDaysWithCompany":
        return (
          consultant?.cumulativeDaysWithCompany ?? consultant?.cumulative_days_with_company ?? null
        );
      case "benchDays":
        return getEffectiveBenchDays(consultant);
      case "hasNextConfirmedAssignment":
        return Boolean(
          consultant?.hasNextConfirmedAssignment ?? consultant?.has_next_confirmed_assignment
        );
      case "averageClientScore":
        return (
          consultant?.averageClientSatisfactionScore ??
          consultant?.average_client_satisfaction_score ??
          null
        );
      default:
        return null;
    }
  };

  const COLUMN_TYPES = {
    name: "string",
    contractType: "string",
    client: "string",
    assignmentEndDate: "date",
    purchaseOrderEndDate: "date",
    cumulativeDaysWithClient: "number",
    numberOfClients: "number",
    cumulativeDaysWithCompany: "number",
    benchDays: "number",
    hasNextConfirmedAssignment: "boolean",
    averageClientScore: "number"
  };

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!sortState.column) {
      return [...items].sort((a, b) => {
        const aTime = getAssignmentEndTimestamp(a);
        const bTime = getAssignmentEndTimestamp(b);
        if (aTime === null && bTime === null) return 0;
        if (aTime === null) return 1;
        if (bTime === null) return -1;
        return aTime - bTime;
      });
    }
    const column = sortState.column;
    const type = COLUMN_TYPES[column] || "string";
    const direction = sortState.direction === "desc" ? -1 : 1;
    const nameOf = (c) => c?.name || c?.fullName || c?.full_name || "";
    return [...items].sort((a, b) => {
      const av = getColumnValue(a, column);
      const bv = getColumnValue(b, column);
      // Boolean values are never "null" - always true or false
      if (type !== "boolean") {
        const aNull = isNullish(av);
        const bNull = isNullish(bv);
        if (aNull && bNull) {
          return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" });
        }
        // Null values sort to the end on asc, start on desc
        if (aNull) return direction === 1 ? 1 : -1;
        if (bNull) return direction === 1 ? -1 : 1;
      }
      const primary = compareValues(av, bv, type);
      if (primary !== 0) return primary * direction;
      // Stable, locale-aware secondary sort by name keeps ties deterministic
      return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" });
    });
    // getColumnValue/COLUMN_TYPES are stable closures within a single render;
    // re-include i18n.language so localized string columns re-sort on language switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortState, i18n.language]);

  const sortLabelProps = {
    sortState,
    onSort: handleSort,
    ariaLabelAsc: t("pilotConsultantTracking.list.table.sortedAscending", {
      defaultValue: "Sorted ascending"
    }),
    ariaLabelDesc: t("pilotConsultantTracking.list.table.sortedDescending", {
      defaultValue: "Sorted descending"
    }),
  };

  const sortHeader = (column, label, align) => (
    <SortLabel column={column} align={align} {...sortLabelProps}>
      {label}
    </SortLabel>
  );

  const columns = [
    { key: "name", label: sortHeader("name", t("pilotConsultantTracking.list.table.name")), width: "15%" },
    { key: "contractType", label: sortHeader("contractType", t("pilotConsultantTracking.list.table.status")), width: "11%" },
    { key: "client", label: sortHeader("client", t("pilotConsultantTracking.list.table.client")), width: "13%" },
    { key: "assignmentEndDate", label: sortHeader("assignmentEndDate", t("pilotConsultantTracking.list.table.assignmentEndDate")), width: "9%" },
    { key: "purchaseOrderEndDate", label: sortHeader("purchaseOrderEndDate", t("pilotConsultantTracking.list.table.purchaseOrderEndDate")), width: "9%" },
    { key: "cumulativeDaysWithClient", label: sortHeader("cumulativeDaysWithClient", t("pilotConsultantTracking.list.table.cumulativeDaysWithClient"), "right"), width: "8%", align: "right" },
    { key: "numberOfClients", label: sortHeader("numberOfClients", t("pilotConsultantTracking.list.table.numberOfClients"), "right"), width: "7%", align: "right" },
    { key: "cumulativeDaysWithCompany", label: sortHeader("cumulativeDaysWithCompany", t("pilotConsultantTracking.list.table.cumulativeDaysWithCompany"), "right"), width: "8%", align: "right" },
    { key: "benchDays", label: sortHeader("benchDays", t("pilotConsultantTracking.list.table.benchDays"), "right"), width: "6%", align: "right" },
    { key: "hasNextConfirmedAssignment", label: sortHeader("hasNextConfirmedAssignment", t("pilotConsultantTracking.list.table.nextAssignment")), width: "8%" },
    { key: "averageClientScore", label: sortHeader("averageClientScore", t("pilotConsultantTracking.list.table.averageClientScore"), "right"), width: "6%", align: "right" },
  ];

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="PI"
        eyebrow={t("pilotConsultantTracking.list.eyebrow", "ASAP Pilot")}
        title={t("pilotConsultantTracking.list.pageTitle")}
      />

      <Box p="20px">
        <Panel
          title={t("pilotConsultantTracking.list.panelTitle", "Consultant Tracking")}
          count={
            loading
              ? t("pilotConsultantTracking.list.summary.loading")
              : t("pilotConsultantTracking.list.summary.total", { count: totalItems })
          }
          actions={
            activeFilterCount > 0 ? (
              <Button variant="secondary" size="sm" h="28px" px="10px" fontSize="11px" onClick={resetFilters}>
                {t("pilotConsultantTracking.list.filters.reset", { defaultValue: "Reset filters" })}
              </Button>
            ) : null
          }
          filters={
            <Box w="100%">
              {/* Primary row: search + dropdown filters */}
              <FilterBar>
                <FilterSearch
                  placeholder={t("pilotConsultantTracking.list.filters.searchPlaceholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
                <Select
                  placeholder={t("pilotConsultantTracking.list.filters.contractTypeAll", { defaultValue: "All contract types" })}
                  value={contractTypeFilter}
                  onChange={(e) => {
                    setContractTypeFilter(e.target.value);
                    setPage(1);
                  }}
                  w={{ base: "100%", sm: "180px" }}
                  flex="0 0 auto"
                  sx={FILTER_SELECT_SX}
                >
                  <option value="permanent_contract">{t("pilotConsultantTracking.list.contractTypes.permanent_contract")}</option>
                  <option value="freelance">{t("pilotConsultantTracking.list.contractTypes.freelance")}</option>
                  <option value="subcontracting">{t("pilotConsultantTracking.list.contractTypes.subcontracting")}</option>
                  <option value="umbrella_company">{t("pilotConsultantTracking.list.contractTypes.umbrella_company")}</option>
                </Select>
                <Select
                  placeholder={t("pilotConsultantTracking.list.filters.clientAll", { defaultValue: "All clients" })}
                  value={clientFilter}
                  onChange={(e) => {
                    setClientFilter(e.target.value);
                    setPage(1);
                  }}
                  w={{ base: "100%", sm: "180px" }}
                  flex="0 0 auto"
                  sx={FILTER_SELECT_SX}
                >
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name || c.companyName || `Client #${c.id}`}
                    </option>
                  ))}
                </Select>
                <Select
                  placeholder={t("pilotConsultantTracking.list.filters.nextMissionAll", { defaultValue: "Next mission: all" })}
                  value={hasNextMission}
                  onChange={(e) => {
                    setHasNextMission(e.target.value);
                    setPage(1);
                  }}
                  w={{ base: "100%", sm: "170px" }}
                  flex="0 0 auto"
                  sx={FILTER_SELECT_SX}
                >
                  <option value="yes">{t("pilotConsultantTracking.list.filters.nextMissionYes", { defaultValue: "Has next mission" })}</option>
                  <option value="no">{t("pilotConsultantTracking.list.filters.nextMissionNo", { defaultValue: "No next mission" })}</option>
                </Select>
                <Select
                  placeholder={t("pilotConsultantTracking.list.filters.assignmentStatusAll", { defaultValue: "All assignment statuses" })}
                  value={assignmentStatusFilter}
                  onChange={(e) => {
                    setAssignmentStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  w={{ base: "100%", sm: "190px" }}
                  flex="0 0 auto"
                  sx={FILTER_SELECT_SX}
                >
                  {MATCH_ASSIGNMENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {t(s.labelKey, { defaultValue: s.label })}
                    </option>
                  ))}
                </Select>
              </FilterBar>

              {/* Secondary row: date / numeric range filters */}
              <Flex gap="12px" flexWrap="wrap" mt="10px">
                <FormControl maxW="320px">
                  <FormLabel
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.05em"
                    color="portal.muted"
                    mb="4px"
                  >
                    {t("pilotConsultantTracking.list.filters.missionEndRangeLabel", { defaultValue: "Mission end date" })}
                  </FormLabel>
                  <HStack spacing="6px">
                    <Input
                      type="date"
                      aria-label={t("pilotConsultantTracking.list.filters.missionEndFromLabel", { defaultValue: "Mission end date from" })}
                      value={missionEndFrom}
                      onChange={(e) => {
                        setMissionEndFrom(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultantTracking.list.filters.to", { defaultValue: "to" })}
                    </Text>
                    <Input
                      type="date"
                      aria-label={t("pilotConsultantTracking.list.filters.missionEndToLabel", { defaultValue: "Mission end date to" })}
                      value={missionEndTo}
                      onChange={(e) => {
                        setMissionEndTo(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                  </HStack>
                </FormControl>
                <FormControl maxW="320px">
                  <FormLabel
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.05em"
                    color="portal.muted"
                    mb="4px"
                  >
                    {t("pilotConsultantTracking.list.filters.poEndRangeLabel", { defaultValue: "Purchase order end date" })}
                  </FormLabel>
                  <HStack spacing="6px">
                    <Input
                      type="date"
                      aria-label={t("pilotConsultantTracking.list.filters.poEndFromLabel", { defaultValue: "PO end date from" })}
                      value={poEndFrom}
                      onChange={(e) => {
                        setPoEndFrom(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultantTracking.list.filters.to", { defaultValue: "to" })}
                    </Text>
                    <Input
                      type="date"
                      aria-label={t("pilotConsultantTracking.list.filters.poEndToLabel", { defaultValue: "PO end date to" })}
                      value={poEndTo}
                      onChange={(e) => {
                        setPoEndTo(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                  </HStack>
                </FormControl>
                <FormControl maxW="260px">
                  <FormLabel
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.05em"
                    color="portal.muted"
                    mb="4px"
                  >
                    {t("pilotConsultantTracking.list.filters.benchDaysRangeLabel", { defaultValue: "Bench days (permanent only)" })}
                  </FormLabel>
                  <HStack spacing="6px">
                    <Input
                      type="number"
                      min={0}
                      aria-label={t("pilotConsultantTracking.list.filters.benchDaysMinLabel", { defaultValue: "Min bench days (permanent only)" })}
                      value={benchDaysMin}
                      onChange={(e) => {
                        setBenchDaysMin(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultantTracking.list.filters.to", { defaultValue: "to" })}
                    </Text>
                    <Input
                      type="number"
                      min={0}
                      value={benchDaysMax}
                      onChange={(e) => {
                        setBenchDaysMax(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                  </HStack>
                </FormControl>
                <FormControl maxW="260px">
                  <FormLabel
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="800"
                    letterSpacing="0.05em"
                    color="portal.muted"
                    mb="4px"
                  >
                    {t("pilotConsultantTracking.list.filters.clientScoreRangeLabel", { defaultValue: "Avg client score" })}
                  </FormLabel>
                  <HStack spacing="6px">
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      step={0.5}
                      value={clientScoreMin}
                      onChange={(e) => {
                        setClientScoreMin(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                    <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                      {t("pilotConsultantTracking.list.filters.to", { defaultValue: "to" })}
                    </Text>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      step={0.5}
                      aria-label={t("pilotConsultantTracking.list.filters.clientScoreMaxLabel", { defaultValue: "Max avg client score" })}
                      value={clientScoreMax}
                      onChange={(e) => {
                        setClientScoreMax(e.target.value);
                        setPage(1);
                      }}
                      sx={RANGE_INPUT_SX}
                    />
                  </HStack>
                </FormControl>
              </Flex>
            </Box>
          }
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(sz) => {
                setPageSize(sz);
                setPage(1);
              }}
            />
          }
        >
          <DataTable
            columns={columns}
            rows={sortedItems}
            loading={loading}
            loadingRows={pageSize > 10 ? 10 : pageSize}
            emptyState={
              <EmptyState
                icon={<FaUserClock />}
                title={t("pilotConsultantTracking.list.table.noItems")}
                hint={t("pilotConsultantTracking.list.table.noItemsHint", {
                  defaultValue: "Adjust the filters or add consultants to see tracking data."
                })}
              />
            }
            renderRow={(c) => {
              const name = c.name || c.fullName || c.full_name || "-";
              const currentClientName =
                c.currentClientName ?? c.current_client_name ?? "-";
              const assignmentEndDate =
                c.currentAssignmentEndDate ??
                c.current_assignment_end_date ??
                c.assignmentEndDate ??
                c.assignment_end_date ??
                "-";
              const purchaseOrderEndDate =
                c.currentPurchaseOrderEndDate ??
                c.current_purchase_order_end_date ??
                c.purchaseOrderEndDate ??
                c.purchase_order_end_date ??
                "-";

              const contractTypeValue =
                c.contractType ??
                c.contract_type ??
                (Array.isArray(c.currentContractTypes || c.current_contract_types)
                  ? (c.currentContractTypes || c.current_contract_types)[0]
                  : null);
              const contractTypeLabel = getContractTypeLabel(contractTypeValue);
              const hasNextConfirmedAssignment =
                c.hasNextConfirmedAssignment ?? c.has_next_confirmed_assignment;

              const cumulativeDaysWithClient =
                c.cumulativeDaysWithClient ?? c.cumulative_days_with_client;
              const cumulativeDaysWithCompany =
                c.cumulativeDaysWithCompany ?? c.cumulative_days_with_company;
              const numberOfClients = c.numberOfClients ?? c.number_of_clients;
              const benchDaysValue = getEffectiveBenchDays(c);
              const averageClientScore =
                c.averageClientSatisfactionScore ??
                c.average_client_satisfaction_score;

              const clientId = c.currentClientId ?? c.current_client_id;

              return (
                <Tr
                  key={c.id}
                  cursor="pointer"
                  onClick={() => handleView(c.id)}
                >
                  <Td fontWeight="700">{name}</Td>
                  <Td>
                    {contractTypeValue ? (
                      <StatusBadge
                        label={contractTypeLabel}
                        colorScheme={CONTRACT_TYPE_COLORS[contractTypeValue] || "gray"}
                      />
                    ) : (
                      "-"
                    )}
                  </Td>
                  <Td>
                    {clientId ? (
                      <Box
                        as="span"
                        color="portal.primary"
                        fontWeight="700"
                        cursor="pointer"
                        _hover={{ textDecoration: "underline" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedClientId(clientId);
                          clientDetailModal.onOpen();
                        }}
                      >
                        {currentClientName}
                      </Box>
                    ) : (
                      currentClientName
                    )}
                  </Td>
                  <Td>{formatDate(assignmentEndDate)}</Td>
                  <Td>{formatDate(purchaseOrderEndDate)}</Td>
                  <Td textAlign="right">{formatNumber(cumulativeDaysWithClient)}</Td>
                  <Td textAlign="right">{formatNumber(numberOfClients)}</Td>
                  <Td textAlign="right">{formatNumber(cumulativeDaysWithCompany)}</Td>
                  <Td textAlign="right">{formatNumber(benchDaysValue)}</Td>
                  <Td>
                    <Tooltip
                      label={
                        hasNextConfirmedAssignment
                          ? t("pilotConsultantTracking.list.badges.nextConfirmed")
                          : t("pilotConsultantTracking.list.badges.noNextConfirmed")
                      }
                    >
                      <Box as="span" display="inline-flex">
                        <StatusBadge
                          label={
                            hasNextConfirmedAssignment
                              ? t("pilotConsultantTracking.list.badges.nextConfirmed")
                              : t("pilotConsultantTracking.list.badges.noNextConfirmed")
                          }
                          colorScheme={hasNextConfirmedAssignment ? "blue" : "gray"}
                        />
                      </Box>
                    </Tooltip>
                  </Td>
                  <Td textAlign="right">
                    {formatNumber(averageClientScore, { maximumFractionDigits: 1 })}
                  </Td>
                </Tr>
              );
            }}
          />
        </Panel>
      </Box>

      <ConsultantDetailModal
        isOpen={consultantDetailModal.isOpen}
        onClose={handleCloseConsultantModal}
        consultantId={selectedConsultantId}
        navigateOnClose={false}
      />
      {selectedClientId && (
        <ClientDetailModal
          isOpen={clientDetailModal.isOpen}
          onClose={() => {
            clientDetailModal.onClose();
            setSelectedClientId(null);
          }}
          clientId={selectedClientId}
        />
      )}
    </Box>
  );
};

export default ConsultantTracking;
